import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Briefcase, Calendar, Check, ChevronDown, ClipboardCheck, Clock, Search, Settings, SlidersVertical, User, Video, X } from "lucide-react";
import { QuiltLogo } from "../QuiltLogo";
import { Clicky, PlayButton, ScreenFrame, ScreenStage } from "./ScreenStage";
import cardSummit from "@/assets/features/videos/card-summit.webp";
import cardWaitingRoom from "@/assets/features/videos/card-waiting-room.webp";
import cardPool from "@/assets/features/videos/card-pool.webp";
import cardMovingDay from "@/assets/features/videos/card-moving-day.webp";
import previewSummit from "@/assets/features/videos/preview-summit.webp";
import scene1 from "@/assets/features/videos/scene-1.webp";
import scene2 from "@/assets/features/videos/scene-2.webp";
import scene3 from "@/assets/features/videos/scene-3.webp";
import scene4 from "@/assets/features/videos/scene-4.webp";

/*
 * A live rebuild of the Quilt Pro content library. It plays a short story on a loop: browse the
 * library, open a video to see the story behind it, then its review. Clicking the card or the close
 * button jumps ahead, and the loop carries on from there. Images stand in for the videos for now.
 */

type Scene = "library" | "story" | "review";

const STEPS: { scene: Scene; ms: number }[] = [
    { scene: "library", ms: 4000 },
    { scene: "story", ms: 6500 },
    { scene: "review", ms: 6500 },
];

type Status = "Approved" | "Pending My Review" | "Rejected";

// The first card is drawn by the hero image, which grows into the detail view
const CARDS: { image?: string; status: Status; creator: string }[] = [
    { status: "Approved", creator: "Grace Turner" },
    { image: cardWaitingRoom, status: "Pending My Review", creator: "Sophia Reed" },
    { image: cardPool, status: "Rejected", creator: "Logan Foster" },
    { image: cardMovingDay, status: "Approved", creator: "Grace Turner" },
];

const STATUS_TEXT: Record<Status, string> = {
    Approved: "text-emerald-600 dark:text-[#22C55E]",
    "Pending My Review": "text-amber-600 dark:text-[#EAB308]",
    Rejected: "text-red-500 dark:text-[#F87171]",
};

const CONCEPTS = ["Values clarification", "Should statements / demandingness", "Acts of kindness (prosocial behaviour)"];

const SCENES = [
    { image: scene1, title: "Summit sky" },
    { image: scene2, title: "The lookout" },
    { image: scene3, title: "A look back" },
    { image: scene4, title: "The way down" },
];

// Mood, style, condition, music and voice tags, each family in its own colour
const TAGS: { label: string; tone: string }[] = [
    ...["Calm", "Reflective", "Melancholic"].map((label) => ({ label, tone: "fuchsia" })),
    ...["Vibrant", "Cinematic", "3D"].map((label) => ({ label, tone: "pink" })),
    ...["Generalized anxiety", "Insomnia"].map((label) => ({ label, tone: "teal" })),
    ...["Ambient", "Drums"].map((label) => ({ label, tone: "indigo" })),
    ...["Slow", "Female", "Motivational", "Low"].map((label) => ({ label, tone: "sky" })),
];

const TAG_TONES: Record<string, string> = {
    fuchsia: "border-fuchsia-500/30 text-fuchsia-600 dark:border-fuchsia-400/25 dark:text-fuchsia-300",
    pink: "border-pink-500/30 text-pink-600 dark:border-pink-400/25 dark:text-pink-400",
    teal: "border-teal-500/30 text-teal-700 dark:border-teal-400/25 dark:text-teal-300",
    indigo: "border-indigo-500/30 text-indigo-600 dark:border-indigo-400/25 dark:text-indigo-300",
    sky: "border-sky-500/30 text-sky-700 dark:border-sky-400/25 dark:text-sky-300",
};

// Where the hero image sits as a library card, and as the big preview
const HERO_CARD = { left: 127, top: 263, width: 351, height: 572, borderRadius: "16px 16px 0px 0px" };
const HERO_DETAIL = { left: 96, top: 82, width: 430, height: 796, borderRadius: "20px 20px 20px 20px", y: 0 };

const ease = [0.22, 1, 0.36, 1] as const;
// Panels sit over the preview, as in the design
const panel = "z-20 rounded-[32px] border border-zinc-200 bg-white/90 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] dark:border-white/[0.07] dark:bg-[#0D0C19]/95 dark:shadow-[0_30px_70px_-20px_rgba(0,0,0,0.7)]";
const label = "text-[20px] font-light text-zinc-400 dark:text-slate-500";

/** A pretend mouse pointer for the autoplay: glides to the first card and clicks it. */
function FakeCursor() {
    return (
        <motion.svg
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-0 top-0 z-30 size-12 drop-shadow-[0_4px_8px_rgba(0,0,0,0.35)]"
            initial={{ x: 900, y: 860, opacity: 0, scale: 1 }}
            animate={{ x: [900, 900, 330, 330, 330], y: [860, 860, 560, 560, 560], opacity: [0, 1, 1, 1, 1], scale: [1, 1, 1, 0.8, 1] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 4.2, times: [0, 0.25, 0.68, 0.82, 0.9], ease: "easeInOut" }}
        >
            <path d="M5 3l14 7.5-6.2 1.6L9.6 18 5 3Z" fill="white" stroke="#111" strokeWidth="1.4" strokeLinejoin="round" />
        </motion.svg>
    );
}

function Sidebar() {
    const items = [Video, Briefcase, User, ClipboardCheck];
    return (
        <div className="absolute inset-y-0 left-0 w-[88px] border-r border-zinc-200 bg-white/60 dark:border-white/[0.06] dark:bg-black/30">
            <QuiltLogo alt="" className="absolute left-[21px] top-10 size-[46px]" />
            <div className="absolute left-1 right-1 top-[126px] flex flex-col gap-[5px]">
                {items.map((Icon, i) => (
                    <Clicky
                        key={i}
                        ring="rounded-xl"
                        className={`flex h-[47px] items-center justify-center rounded-xl transition-colors ${i === 0
                            ? "bg-zinc-100 text-zinc-900 dark:bg-white/[0.07] dark:text-white"
                            : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:text-slate-500 dark:hover:bg-white/[0.05] dark:hover:text-slate-200"
                            }`}
                    >
                        <Icon className="size-6" strokeWidth={1.5} />
                        {i === 0 && <span className="absolute right-2 top-1.5 size-[7px] rounded-full bg-[#F755C1]" />}
                    </Clicky>
                ))}
            </div>
        </div>
    );
}

function Library({ lift, onOpen, onLift }: { lift: boolean; onOpen: () => void; onLift: (on: boolean) => void }) {
    const filters = [
        { label: "Status", width: 109 },
        { label: "Created by", width: 147 },
        { label: "Creator", width: 111 },
        { label: "Date of creation", width: 187 },
    ];
    return (
        <>
            <Sidebar />
            <div className="absolute left-[129px] top-[30px]">
                <h3 className="text-[28px] font-semibold text-zinc-900 dark:text-white">Videos</h3>
                <p className="mt-1 text-[16px] text-zinc-500 dark:text-slate-500">Every video across every review stage.</p>
            </div>
            <div className="absolute left-[88px] right-0 top-[128px] h-px bg-zinc-200 dark:bg-white/[0.07]" />

            <div className="absolute left-[128px] top-[154px] flex gap-2.5">
                <Clicky ring="rounded-xl" className="flex h-[47px] w-[274px] items-center gap-2.5 rounded-xl border border-zinc-200 bg-white px-4 text-[16px] text-zinc-400 transition-colors hover:border-zinc-300 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-slate-500 dark:hover:border-white/15">
                    <Search className="size-[18px]" />
                    Search by tags...
                </Clicky>
                {filters.map((f) => (
                    <Clicky
                        key={f.label}
                        ring="rounded-xl"
                        className="group/chip flex h-[47px] items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white text-[16px] text-zinc-600 transition-colors hover:border-purple-400 hover:text-zinc-900 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-slate-300 dark:hover:border-purple-400/50 dark:hover:text-white"
                        style={{ width: f.width }}
                    >
                        {f.label}
                        <ChevronDown className="size-[18px] transition-transform duration-200 group-hover/chip:translate-y-0.5" />
                    </Clicky>
                ))}
            </div>
            <p className="absolute left-[129px] top-[218px] text-[16px] font-medium text-zinc-600 dark:text-slate-300">29 videos found</p>

            {/* Cards, fading out past the right edge */}
            <div className="absolute inset-0 [mask-image:linear-gradient(to_right,black_78%,transparent_99%)]">
                {CARDS.map((card, i) => {
                    const first = i === 0;
                    return (
                        <motion.div
                            key={i}
                            className="absolute top-[263px] h-[664px] w-[351px]"
                            style={{ left: 127 + i * 372 }}
                            animate={{ y: first && lift ? -8 : 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 24 }}
                            onHoverStart={() => first && onLift(true)}
                            onHoverEnd={() => first && onLift(false)}
                        >
                            <Clicky
                                ring="rounded-2xl"
                                onClick={first ? onOpen : undefined}
                                className={`group/card h-full overflow-hidden rounded-2xl border bg-white transition-[border-color,box-shadow] duration-300 dark:bg-[#0D0C19] ${first && lift
                                    ? "border-purple-400/70 shadow-[0_20px_50px_-15px_rgba(140,0,226,0.45)] dark:border-purple-400/50"
                                    : "border-zinc-200 hover:border-zinc-300 dark:border-white/[0.08] dark:hover:border-white/20"
                                    }`}
                            >
                                <div className="h-[572px] overflow-hidden">
                                    {card.image && (
                                        <img src={card.image} alt="" draggable={false} className="size-full object-cover transition-transform duration-500 group-hover/card:scale-[1.03]" />
                                    )}
                                </div>
                                <div className="px-4 pt-[18px]">
                                    <p className={`text-[24px] font-medium leading-tight ${STATUS_TEXT[card.status]}`}>{card.status}</p>
                                    <p className="mt-1.5 text-[18px] text-zinc-400 dark:text-slate-500">
                                        {card.creator} <span className="px-1">•</span> Manual
                                    </p>
                                </div>
                            </Clicky>
                        </motion.div>
                    );
                })}
            </div>
        </>
    );
}

function StoryPanels({ concept, onConcept }: { concept: number; onConcept: (i: number) => void }) {
    return (
        <>
            <motion.div className={`absolute left-[468px] top-[82px] h-[250px] w-[660px] p-6 ${panel}`} {...slideIn(0)}>
                <p className={label}>Concept</p>
                <div className="mt-3 flex flex-col gap-3">
                    {CONCEPTS.map((c, i) => (
                        <Clicky
                            key={c}
                            ring="rounded-2xl"
                            onClick={() => onConcept(i)}
                            className={`flex h-12 items-center justify-between rounded-2xl border px-5 text-[19px] transition-colors ${i === concept
                                ? "border-purple-400/40 bg-purple-500/[0.06] text-zinc-900 dark:border-purple-300/25 dark:bg-white/[0.07] dark:text-white"
                                : "border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-white/[0.09] dark:text-slate-300 dark:hover:bg-white/[0.03]"
                                }`}
                        >
                            {c}
                            <AnimatePresence>
                                {i === concept && (
                                    <motion.span initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="text-[17px] text-zinc-400 dark:text-slate-500">
                                        Selected
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </Clicky>
                    ))}
                </div>
            </motion.div>

            <motion.div className={`absolute left-[468px] top-[346px] h-[319px] w-[660px] px-6 py-6 ${panel}`} {...slideIn(1)}>
                <p className={label}>Idea</p>
                <p className="mt-2 text-[19px] font-light leading-[31px] text-zinc-800 dark:text-slate-200">
                    At a hiking summit, a man reflects on how far he has come. An old photo reminds him of past struggles, prompting him to
                    consider what matters most: his health, independence, and loved ones. As he heads back down, he feels clearer about the
                    values guiding his choices. <span className="cursor-pointer text-zinc-400 hover:text-zinc-600 dark:text-slate-500 dark:hover:text-slate-300">See less...</span>
                </p>
                <p className={`mt-5 ${label}`}>Description</p>
                <p className="mt-1.5 text-[19px] font-light text-zinc-800 dark:text-slate-200">Sharing a moment of strength.</p>
            </motion.div>

            <motion.div className={`absolute left-[468px] top-[679px] h-[199px] w-[660px] px-6 py-5 ${panel}`} {...slideIn(2)}>
                <div className="flex items-baseline justify-between">
                    <p className={label}>Storyboard</p>
                    <p className="text-[17px] text-zinc-400 dark:text-slate-500">4 scenes</p>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-[13px]">
                    {SCENES.map((s, i) => (
                        <Clicky key={s.title} ring="rounded-xl" className="group/scene">
                            <div className="h-[87px] overflow-hidden rounded-xl">
                                <motion.img
                                    src={s.image}
                                    alt=""
                                    draggable={false}
                                    className="size-full object-cover transition-transform duration-500 group-hover/scene:scale-110"
                                    initial={{ opacity: 0, scale: 1.15 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.55 + i * 0.12, duration: 0.6, ease }}
                                />
                            </div>
                            <p className="mt-2 text-[13px] text-zinc-700 dark:text-slate-300">
                                <span className="mr-1 text-zinc-400 dark:text-slate-500">0{i + 1}</span>
                                {s.title}
                            </p>
                        </Clicky>
                    ))}
                </div>
            </motion.div>
        </>
    );
}

function Meta({ icon: Icon, name, value }: { icon: typeof Clock; name: string; value: string }) {
    return (
        <div className="flex items-center gap-2.5 text-[17px]">
            <Icon className="size-[18px] text-zinc-300 dark:text-slate-600" strokeWidth={1.5} />
            <span className="text-zinc-400 dark:text-slate-500">{name}</span>
            <span className="text-zinc-800 dark:text-slate-100">{value}</span>
        </div>
    );
}

function StepCheck({ delay }: { delay: number }) {
    return (
        <motion.span
            className="flex size-[38px] items-center justify-center rounded-full border-2 border-zinc-700 bg-white text-emerald-500 dark:border-slate-400/70 dark:bg-transparent dark:text-[#22C55E]"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay, type: "spring", stiffness: 400, damping: 18 }}
        >
            <Check className="size-5" strokeWidth={3} />
        </motion.span>
    );
}

function ReviewPanel({ onClose }: { onClose: () => void }) {
    return (
        <motion.div className={`absolute left-[454px] top-[133px] h-[694px] w-[672px] rounded-[40px] p-7 ${panel}`} {...slideIn(0)}>
            <div className="h-[410px] rounded-[28px] bg-zinc-50 px-6 pt-6 dark:bg-white/[0.04]">
                <div className="flex items-start justify-between">
                    <motion.p
                        className="text-[24px] font-semibold text-emerald-600 dark:text-[#22C55E]"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.5, duration: 0.4 }}
                    >
                        Approved
                    </motion.p>
                    <Clicky onClick={onClose} className="-mr-1 -mt-0.5 flex size-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-200/70 hover:text-zinc-800 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white">
                        <X className="size-6" strokeWidth={1.5} />
                    </Clicky>
                </div>
                <p className="mt-1 text-[16px] text-zinc-400 dark:text-slate-500">Editing as Jordan Lee (Creator)</p>
                <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3.5">
                    <Meta icon={SlidersVertical} name="Content Type" value="EMI" />
                    <Meta icon={Settings} name="Source" value="Generated" />
                    <Meta icon={Clock} name="Duration" value="45s" />
                    <Meta icon={Calendar} name="Created" value="May 20, 2026" />
                </div>

                {/* Create, then review: the checks land one after the other */}
                <div className="mt-5 h-[200px] rounded-[20px] border border-zinc-200 dark:border-white/[0.1]">
                    <div className="flex h-[126px] items-start justify-center pt-[28px]">
                        <div className="flex flex-col items-center gap-2">
                            <StepCheck delay={0.35} />
                            <span className="text-[16px] text-zinc-700 dark:text-slate-200">Create</span>
                        </div>
                        <div className="relative mt-[18px] h-px w-[92px] bg-zinc-200 dark:bg-white/10">
                            <motion.div
                                className="absolute inset-0 origin-left bg-zinc-500 dark:bg-slate-400"
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ delay: 0.6, duration: 0.5, ease }}
                            />
                            <motion.span
                                className="absolute -right-0.5 -top-[3.5px] border-y-[4px] border-l-[6px] border-y-transparent border-l-zinc-500 dark:border-l-slate-400"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1.05 }}
                            />
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <StepCheck delay={1.1} />
                            <span className="text-[16px] text-zinc-400 dark:text-slate-500">Review</span>
                        </div>
                    </div>
                    <div className="flex h-[74px] items-center justify-center border-t border-zinc-200 dark:border-white/[0.1]">
                        <motion.span
                            className="flex h-[30px] items-center gap-2 rounded-full bg-white pl-1 pr-3 text-[16px] shadow-sm dark:bg-white/[0.07] dark:shadow-none"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1.35, duration: 0.35 }}
                        >
                            <QuiltLogo alt="" className="size-[22px] rounded-full bg-white p-0.5" />
                            <span className="text-zinc-800 dark:text-slate-100">Jordan Lee (Me)</span>
                            <span className="text-emerald-600 dark:text-[#22C55E]">Approved</span>
                        </motion.span>
                    </div>
                </div>
            </div>

            <div className="mt-5 h-[208px] rounded-[28px] bg-zinc-50 px-6 pt-5 dark:bg-white/[0.04]">
                <p className={label}>Tags</p>
                <div className="mt-3 flex flex-wrap gap-x-2.5 gap-y-2.5">
                    {TAGS.map((t, i) => (
                        <motion.span
                            key={t.label}
                            className={`flex h-[33px] cursor-pointer items-center rounded-full border px-3.5 text-[17px] transition-colors hover:bg-zinc-100 dark:hover:bg-white/[0.05] ${TAG_TONES[t.tone]}`}
                            initial={{ opacity: 0, y: 8, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ delay: 0.5 + i * 0.045, duration: 0.35, ease }}
                        >
                            {t.label}
                        </motion.span>
                    ))}
                </div>
            </div>
        </motion.div>
    );
}

/** Panels slide in from the right, one after another, and drift out to the right when they leave. */
function slideIn(i: number) {
    return {
        initial: { opacity: 0, x: 60 },
        animate: { opacity: 1, x: 0, transition: { delay: 0.25 + i * 0.1, duration: 0.6, ease } },
        exit: { opacity: 0, x: 30, transition: { duration: 0.3 } },
    };
}

function Screen({
    scene,
    auto,
    onScene,
}: {
    scene: Scene;
    auto: boolean;
    onScene: (s: Scene) => void;
}) {
    const [hover, setHover] = useState(false);
    const [pointing, setPointing] = useState(false);
    const [concept, setConcept] = useState(0);
    const detail = scene !== "library";

    // In autoplay the pretend pointer reaches the first card just before it opens
    useEffect(() => {
        setPointing(false);
        if (!auto || scene !== "library") return;
        const t = setTimeout(() => setPointing(true), 2900);
        return () => clearTimeout(t);
    }, [auto, scene]);

    return (
        <ScreenFrame>
            <motion.div
                className="absolute inset-0"
                animate={detail ? { opacity: 0, scale: 0.97 } : { opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease }}
                style={{ pointerEvents: detail ? "none" : "auto" }}
            >
                <Library lift={hover || pointing} onLift={setHover} onOpen={() => onScene("story")} />
            </motion.div>

            {/* The phone bezel behind the big preview */}
            <motion.div
                className="absolute left-[72px] top-[58px] h-[842px] w-[476px] rounded-[44px] border border-zinc-200 bg-zinc-100 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)] dark:border-white/[0.07] dark:bg-black/60 dark:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
                animate={detail ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.55, ease, delay: detail ? 0.1 : 0 }}
            />

            {/* The hero: the first library card, which grows into the preview */}
            <motion.div
                className="absolute z-10 overflow-hidden"
                initial={false}
                animate={detail ? HERO_DETAIL : { ...HERO_CARD, y: hover || pointing ? -8 : 0 }}
                transition={{ duration: 0.75, ease }}
                style={{ pointerEvents: detail ? "auto" : "none" }}
            >
                {/* The card still carries its play badge and length; the preview is the clean frame */}
                <img src={cardSummit} alt="" draggable={false} className="absolute inset-0 size-full object-cover" />
                <motion.img
                    src={previewSummit}
                    alt=""
                    draggable={false}
                    className="absolute inset-0 size-full object-cover"
                    initial={false}
                    animate={{ opacity: detail ? 1 : 0 }}
                    transition={{ duration: 0.4, delay: detail ? 0.15 : 0 }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <Clicky>
                        <motion.div animate={detail ? { scale: [1, 1.06, 1] } : { scale: 1 }} transition={{ duration: 2.4, repeat: detail ? Infinity : 0, ease: "easeInOut" }}>
                            <PlayButton size={detail ? 90 : 0} />
                        </motion.div>
                    </Clicky>
                </div>
            </motion.div>

            <AnimatePresence>
                {scene === "story" && <StoryPanels key="story" concept={concept} onConcept={setConcept} />}
                {scene === "review" && <ReviewPanel key="review" onClose={() => onScene("library")} />}
            </AnimatePresence>

            <AnimatePresence>{auto && scene === "library" && <FakeCursor key="cursor" />}</AnimatePresence>
        </ScreenFrame>
    );
}

export function VideoLibraryMock({ reserve }: { reserve?: string }) {
    const [step, setStep] = useState(0);
    const wrap = useRef<HTMLDivElement>(null);
    const inView = useInView(wrap, { amount: 0.5 });
    const reduceMotion = useReducedMotion();
    const playing = inView && !reduceMotion;

    // Each step hands over to the next while the screen is on screen, round and round
    useEffect(() => {
        if (!playing) return;
        const t = setTimeout(() => setStep((s) => (s + 1) % STEPS.length), STEPS[step].ms);
        return () => clearTimeout(t);
    }, [playing, step]);

    const goTo = (scene: Scene) => setStep(STEPS.findIndex((s) => s.scene === scene));

    return (
        <div ref={wrap}>
            <ScreenStage reserve={reserve} label="Quilt Pro video library: every video with its review status, and a video's concept, idea, storyboard, approval and tags.">
                {() => <Screen scene={STEPS[step].scene} auto={playing} onScene={goTo} />}
            </ScreenStage>
        </div>
    );
}

export default VideoLibraryMock;
